/**
 * Best Hand Display
 * Shows the viewer's actual current best hand on one axis (rank or
 * jewel/"color") — the resolved hand and its 5 cards. Used twice in
 * PlayerPanel, once per axis.
 *
 * Desktop / big screens: the hand, then all 8 lines' potentials, always
 * shown. (Phones use PlayerPanel's own one-box layout instead.)
 */

import React from 'react';
import { motion } from 'framer-motion';
import type { Card } from '@pokercubed/shared';
import PlayingCard from './PlayingCard';
import { formatChips } from '../../utils/format';

/** 'out': a line that can't win — out of play (Crossed), or taken away (Snare/Flawed/Fray). */
type LineStatus = 'empty' | 'partial' | 'complete' | 'out';

/** One of the 8 lines in the overview: its number (1-8), how far along, and what it says. */
export interface LineRow {
  line: number;
  status: LineStatus;
  label: string;
  /** For sorting, strongest first. */
  strength: number;
}

const STATUS_CLASS: Record<LineStatus, string> = {
  empty: 'text-gray-600',
  partial: 'text-amber-400',
  complete: 'text-green-400 font-semibold',
  out: 'text-gray-600 italic',
};

interface BestHandDisplayProps {
  label: string;
  /** Any evaluation shape with a `name` and its 5 `cards` — HandEvaluation or JewelHandEvaluation. */
  evaluation: { name: string; cards: Card[] } | null;
  accentClass?: string;
  /** What would most improve it — the same hint the phone shows ("Need an A♦ → for a Full House · line 3"). */
  hint?: string;
  /** Each line's potential for this same axis, strongest first (line = its number, 1-8) — desktop only. */
  potentials?: LineRow[];
  /** This axis's half of the pot (Hi/Lo split — see poker-app-spec.md Section 3). */
  potShare?: number;
  /** Which of the 8 numbered paylines this evaluation's winning line is (see useBestHandsSoFar's getLineNumber). */
  lineNumber?: number | null;
  /** True once the hand has ended and the viewer actually won this axis's half of the pot. */
  won?: boolean;
}

const BestHandDisplay: React.FC<BestHandDisplayProps> = ({
  label,
  evaluation,
  accentClass = 'border-yellow-600 text-yellow-400',
  potentials,
  hint,
  potShare,
  lineNumber,
  won,
}) => {
  const [borderClass, textClass = ''] = accentClass.split(' ');
  const hasLine = lineNumber !== null && lineNumber !== undefined;

  const hasLines = !!potentials && potentials.length > 0;
  return (
    <div
      className={`min-w-0 p-2 sm:p-3 short:!p-1.5 bg-gray-900/60 rounded-lg border-2 ${borderClass} ${
        won ? 'ring-2 ring-yellow-400/70 bg-yellow-900/20' : ''
      }`}
    >
      <div className="flex items-baseline justify-between mb-1">
        <div className="flex items-center gap-1.5 text-[10px] sm:text-xs font-semibold uppercase tracking-wider text-gray-400">
          {label}
          {won && <span className="text-yellow-300 normal-case tracking-normal">🏆 You Won!</span>}
        </div>
        {potShare !== undefined && (
          <div className="text-[10px] sm:text-xs font-bold text-yellow-400">{formatChips(potShare)}</div>
        )}
      </div>

      {evaluation ? (
        <motion.div
          key={evaluation.name}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-1 flex-wrap"
        >
          <div className="flex gap-1">
            {evaluation.cards.map((card, i) => (
              <PlayingCard key={i} card={card} className="w-6 sm:w-12" />
            ))}
          </div>
          <div className="ml-1 leading-tight min-w-0">
            <div className={`text-xs sm:text-sm font-bold ${textClass}`}>{evaluation.name}</div>
            {hasLine && <div className="text-[10px] text-gray-400">Line {lineNumber}</div>}
          </div>
        </motion.div>
      ) : (
        <div className="text-xs sm:text-sm text-gray-500 italic py-1">Not yet determined</div>
      )}

      {hint && (
        <div data-testid="best-hand-next" className="mt-1.5 text-xs sm:text-sm text-gray-200">
          {hint.split(' → ').map((part, i) => (
            <span key={i}>{i > 0 && ' '}{part}</span>
          ))}
        </div>
      )}

      {/* All 8 lines, always shown on a big screen — line by line, as an overview. */}
      {hasLines && (
        <div data-testid="best-hand-lines" className="mt-2 pt-2 border-t border-gray-700/60 grid grid-cols-4 gap-x-1 gap-y-1">
          {potentials!.map((p) => (
            <div key={p.line} className="text-center leading-tight">
              <div className="text-[9px] text-gray-500 font-bold">{p.line}</div>
              <div className={`text-[8px] sm:text-[9px] ${STATUS_CLASS[p.status]}`}>{p.label}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BestHandDisplay;
